/**
 * Task #1572 / #1798 / #2181 — Ads Express auto-naming convention:
 *   {Persona}_{Product}_{Narrative Angle}[_{Sale Name}]_{Type}_PersonaOS_{YYMMDD}{Letter}
 * e.g. "Gen Z Ingredient Skeptic_Whipped Tallow Balm_Your Instinct Was Right_Static_PersonaOS_260722A"
 *
 * The optional Sale Name segment (Task #2181) appears only when the run had
 * a sale/discount attached — inserted between the angle and the type with
 * the same trimming/underscore rules as every other segment. Runs without a
 * sale produce byte-identical names to the pre-#2181 format.
 *
 * The `PersonaOS` segment sits immediately before the date code so media
 * buyers can spot PersonaOS-made creatives at a glance.
 *
 * The letter follows render order within a single run (A, B, … Z, AA, AB, …)
 * and restarts at A for every new run. Aspect-ratio variants of the same ad
 * unit share one letter — the download filename's ratio suffix disambiguates.
 */

/** Tool tag inserted right before the date code. */
export const ADS_EXPRESS_TOOL_TAG = "PersonaOS";

/**
 * Name a generated iteration when canonical persona/product/angle snapshots
 * are unavailable. The source label is preserved verbatim (apart from outer
 * whitespace) and only the product marker is appended. This deliberately does
 * not try to parse or invent attribution metadata.
 */
export function buildPersonaOSIterationFallbackName(
  sourceName: string | null | undefined,
): string | null {
  const preserved = sourceName?.trim();
  if (!preserved) return null;
  // Canonical names and buyer wrapper names may already carry the marker.
  // Preserve either exactly rather than producing a duplicate tag.
  if (/(?:^|[^a-z0-9])personaos(?:$|[^a-z0-9])/i.test(preserved)) {
    return preserved;
  }
  return `${preserved}_${ADS_EXPRESS_TOOL_TAG}`;
}

/** 0 -> "A", 25 -> "Z", 26 -> "AA", 27 -> "AB", … (bijective base-26). */
export function adRunLetter(index: number): string {
  let n = Math.max(0, Math.floor(index)) + 1;
  let out = "";
  while (n > 0) {
    n -= 1;
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26);
  }
  return out;
}

/** Format a date as YYMMDD (e.g. July 22, 2026 -> "260722"). */
export function adRunDateCode(date: Date): string {
  const yy = String(date.getFullYear() % 100).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yy}${mm}${dd}`;
}

/** Trim a name part and strip underscores so parts stay unambiguous. */
function cleanPart(part: string): string {
  return part.replace(/_/g, " ").replace(/\s+/g, " ").trim();
}

export function buildAdsExpressAdName(opts: {
  personaName: string;
  productName: string;
  angleName: string;
  /** Task #2181 — sale/discount name; null/empty = no sale segment (legacy format). */
  saleName?: string | null;
  type?: string;
  date: Date;
  index: number;
}): string {
  const { personaName, productName, angleName, saleName, type = "Static", date, index } = opts;
  const cleanedSale = saleName ? cleanPart(saleName) : "";
  return [
    cleanPart(personaName),
    cleanPart(productName),
    cleanPart(angleName),
    ...(cleanedSale ? [cleanedSale] : []),
    type,
    ADS_EXPRESS_TOOL_TAG,
    `${adRunDateCode(date)}${adRunLetter(index)}`,
  ].join("_");
}

/**
 * Task #2193 — parse a full PersonaOS-convention ad name back into its
 * segments:
 *   {Persona}_{Product}_{Angle}[_{Sale Name}]_{Type}[_PersonaOS]_{YYMMDD}{Letter}
 *
 * The parse anchors on the trailing date code, then walks the underscore
 * segments backwards (optional PersonaOS tag, then Type), leaving the head
 * segments. Because the builder strips underscores out of every part
 * (cleanPart), a well-formed name splits unambiguously:
 *   4 head segments → persona, product, angle, sale
 *   3 head segments → persona, product, angle (no sale)
 * Anything else (too few/too many segments, no parseable tail) returns null —
 * callers should validate the extracted angle against the brand's stored
 * angle names rather than trusting positions blindly.
 */
export interface ParsedPersonaOSAdName {
  persona: string;
  product: string;
  angle: string;
  sale: string | null;
  type: string;
  personaOsTagged: boolean; // false for legacy (pre-#1798) tails
  dateCode: string;
  letter: string;
}

export function parsePersonaOSAdName(
  name: string | null | undefined,
): ParsedPersonaOSAdName | null {
  if (!name) return null;
  // Launched ads sometimes carry a trailing aspect-ratio suffix from the
  // download filename (e.g. "..._260729I_4x5") — strip it before anchoring.
  const trimmed = name.trim().replace(/_\d+x\d+$/, "");
  const m = /^(.+)_(\d{6})([A-Z]+)$/.exec(trimmed);
  if (!m) return null;
  const parts = m[1].split("_").map((p) => p.trim()).filter(Boolean);
  let personaOsTagged = false;
  if (parts[parts.length - 1] === ADS_EXPRESS_TOOL_TAG) {
    personaOsTagged = true;
    parts.pop();
  }
  // Minimum: persona, product, angle, type. Maximum adds the sale segment.
  if (parts.length < 4 || parts.length > 5) return null;
  const type = parts.pop()!;
  const [persona, product, angle, sale] = parts;
  return {
    persona,
    product,
    angle,
    sale: sale ?? null,
    type,
    personaOsTagged,
    dateCode: m[2],
    letter: m[3],
  };
}

// Trailing date-code segment: optional "_PersonaOS" segment (post-#1798
// names) immediately before "_{YYMMDD}{LETTER}" at the very end.
const DATE_CODE_TAIL_RE = /_(?:PersonaOS_)?(\d{6})([A-Z]+)$/;

/**
 * Pull the "{YYMMDD}{Letter}" date code out of an existing default name
 * (old "…_Static_260722A" or new "…_Static_PersonaOS_260722A" format).
 * Returns null when the name doesn't end in a parseable date code.
 */
export function parseAdNameDateCode(
  name: string | null | undefined,
): { dateCode: string; letter: string } | null {
  if (!name) return null;
  const m = DATE_CODE_TAIL_RE.exec(name.trim());
  if (!m) return null;
  return { dateCode: m[1], letter: m[2] };
}

/**
 * Task #1798 — rebuild an existing ad's default name to the current
 * convention (inserting the PersonaOS tag), preserving the original
 * date code + letter when parseable from the current default name and
 * otherwise falling back to the run's creation date + render-order index.
 *
 * Returns null when the ad is ineligible (missing snapshots, or no
 * parseable date code AND no usable fallback index). Idempotent: running
 * it on an already-regenerated name yields the same name — the PersonaOS
 * segment is never inserted twice.
 */
export function regenerateAdsExpressAdName(opts: {
  currentDefaultName: string | null | undefined;
  personaName: string | null | undefined;
  productName: string | null | undefined;
  angleName: string | null | undefined;
  /** Task #2181 — the ad's sale-name snapshot; null/empty = no sale segment. */
  saleName?: string | null;
  type?: string;
  fallbackDate: Date;
  /** Ad's position in the run's selected-units order; -1/undefined = unknown. */
  index?: number;
}): string | null {
  const { currentDefaultName, personaName, productName, angleName, saleName, type = "Static", fallbackDate, index } = opts;
  if (!personaName?.trim() || !productName?.trim() || !angleName?.trim()) return null;
  const parsed = parseAdNameDateCode(currentDefaultName);
  let tail: string;
  if (parsed) {
    tail = `${parsed.dateCode}${parsed.letter}`;
  } else if (index !== undefined && index >= 0) {
    tail = `${adRunDateCode(fallbackDate)}${adRunLetter(index)}`;
  } else {
    return null;
  }
  const cleanedSale = saleName ? cleanPart(saleName) : "";
  return [
    cleanPart(personaName),
    cleanPart(productName),
    cleanPart(angleName),
    ...(cleanedSale ? [cleanedSale] : []),
    type,
    ADS_EXPRESS_TOOL_TAG,
    tail,
  ].join("_");
}
